import { array, object, bool } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';

import { get } from 'ravenjs/utils/lodash';

import CategoryLabels from 'components/CategoryLabels';
import { CONTENT_TYPES_MAPPED } from 'constants/content';
import { selectors as userSelectors } from 'modules/user';
import {
    getContentNameByContentType,
    getIconByDocumentType,
    getInfoDocumentType,
} from 'utils/content';
import { formatCategoriesAndSubcategories } from 'utils/documents';
import { sanitizeMarkup } from 'utils/sanitize';
import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';

const TypeAheadSuggestionStyled = styled.div`
    background-color: ${MIGRATED_COLORS.white};
    border-bottom: 1px solid ${MIGRATED_COLORS.light};
    cursor: pointer;
    font-size: 0.95rem;
    overflow: hidden;
    padding: 0.75rem 1rem;
    width: 100%;
    &.active {
        background-color: ${MIGRATED_COLORS.lightest};
    }
`;

const PreviewTextStyled = styled.div`
    & strong {
        color: ${MIGRATED_COLORS.tan};
    }
    margin-bottom: 0.4rem;
`;

const ContentTypeContainerStyled = styled.div`
    display: flex;
`;

const ContentTypeStyled = styled.div`
    color: ${MIGRATED_COLORS.black};
    margin-left: 0.5rem;
    font-family: RobotoMono;
    font-weight: bold;
    font-size: 0.8rem;
`;

const TitleStyled = styled.div`
    color: ${MIGRATED_COLORS.dark};
    font-size: 0.7rem;
    margin-top: 0.15rem;
`;

const TypeAheadSuggestion = ({ contentTypes, suggestion, isHighlighted, isHelpCenter }) => {
    const { description, highlights = [], categories = [] } = suggestion;
    const { previewText } = highlights.length ? highlights[0] : { previewText: '' };
    const content = isHelpCenter ? description : previewText;
    const sanitizedPreviewTextElement = (
        <PreviewTextStyled dangerouslySetInnerHTML={sanitizeMarkup(content)} />
    );

    const getStyledContentType = () => {
        const { contentType, documentType } = suggestion;
        const infoDocumentType = getInfoDocumentType(contentTypes, contentType, documentType);
        const currentContentType =
            suggestion.contentType === CONTENT_TYPES_MAPPED.DOCUMENT ? documentType : contentType;

        return (
            getContentNameByContentType(currentContentType) ||
            get(infoDocumentType, 'documentTypeLabel', '')
        );
    };
    const parentCategories = formatCategoriesAndSubcategories(categories);

    return (
        <TypeAheadSuggestionStyled className={isHighlighted ? 'active' : ''}>
            {sanitizedPreviewTextElement}
            <ContentTypeContainerStyled>
                <div>
                    {getIconByDocumentType(suggestion.documentType || suggestion.contentType, {
                        height: '13',
                        width: '13',
                    })}
                </div>
                <ContentTypeStyled>{getStyledContentType()}</ContentTypeStyled>
            </ContentTypeContainerStyled>
            {parentCategories && <CategoryLabels categories={parentCategories} />}
            <TitleStyled>{isHelpCenter ? suggestion.name : suggestion.title}</TitleStyled>
        </TypeAheadSuggestionStyled>
    );
};

TypeAheadSuggestion.defaultProps = {
    isHelpCenter: false,
};

TypeAheadSuggestion.propTypes = {
    contentTypes: array.isRequired,
    suggestion: object.isRequired,
    isHighlighted: bool.isRequired,
    isHelpCenter: bool,
};

const mapStateToProps = createStructuredSelector({
    contentTypes: userSelectors.getContentTypes,
});

export default connect(mapStateToProps, null)(TypeAheadSuggestion);
